import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, ArrowRight, Facebook, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Pinterest brand icon (lucide doesn't include Pinterest, so lightweight inline SVG)
const Pinterest = ({ size = 16, strokeWidth = 1.5 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M8 20l4-9" />
    <path d="M9 11a3 3 0 1 0 5.5 1.5" />
  </svg>
);

const SOCIALS = [
  ["Instagram", Instagram, "https://www.instagram.com/wear.vyronofficial/"],
  ["Facebook", Facebook, "https://www.facebook.com/wear.vyronofficial"],
  ["YouTube", Youtube, "https://www.youtube.com/@wear.vyronofficial"],
  ["Pinterest", Pinterest, "https://www.pinterest.com/wear.vyronofficial/"],
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#F5F5F5] border-t border-black/5 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Newsletter */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-black/10">
          <div className="md:col-span-6">
            <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Join the VYRON list.
            </h3>
            <p className="text-sm text-muted-foreground mt-4 max-w-md">
              Early access to drops, private previews, and 10% off your first order.
            </p>
          </div>
          <div className="md:col-span-6 flex items-end">
            <form onSubmit={subscribe} className="w-full flex gap-2 sm:gap-3" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 min-w-0 bg-white border border-black/15 rounded-full px-5 sm:px-6 py-4 text-sm outline-none focus:border-black transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="shrink-0 bg-black text-white rounded-full px-5 sm:px-7 py-4 text-xs uppercase tracking-[0.15em] hover:bg-black/85 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? "…" : <>Subscribe <ArrowRight size={15} /></>}
              </button>
            </form>
          </div>
        </div>

        {/* Links */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 py-16">
          <div className="md:col-span-4">
            <p className="font-display text-2xl tracking-[0.3em] uppercase">VYRON</p>
            <p className="text-sm text-muted-foreground mt-4 max-w-xs leading-relaxed">
              Premium eyewear crafted in Chandigarh, Mohali, India. See, buy and style.
            </p>
          </div>
          <FooterCol title="Shop" links={[["Shop All", "/shop"], ["New Arrivals", "/collection/new-arrivals"], ["Best Sellers", "/collection/best-sellers"], ["Signature Collection", "/collection/signature-collection"]]} />
          <FooterCol title="Company" links={[["About", "/about"], ["Contact", "/contact"], ["FAQ", "/faq"], ["Track Order", "/track"]]} />
          <FooterCol title="Legal" links={[["Privacy Policy", "/privacy"], ["Replace Policy", "/refund-policy"], ["Shipping Policy", "/shipping-policy"], ["Terms & Conditions", "/terms"]]} />
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">Follow Us</p>
            <a href="https://www.instagram.com/wear.vyronofficial/" target="_blank" rel="noreferrer" data-testid="footer-instagram"
              className="text-sm link-underline inline-block mb-4">@wear.vyronofficial</a>
            <div className="flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map(([label, Icon, href]) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                  <Icon size={14} strokeWidth={1.5} />
                </a>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">vyronofficial6@gmail.com</p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-3 pt-8 border-t border-black/10 text-xs text-muted-foreground uppercase tracking-widest">
          <span>© {new Date().getFullYear()} VYRON</span>
          <span>Chandigarh, Mohali · Shipped worldwide</span>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div className="md:col-span-2">
    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">{title}</p>
    <ul className="space-y-3">
      {links.map(([l, to]) => (
        <li key={to}>
          <Link to={to} className="text-sm link-underline text-black/80">{l}</Link>
        </li>
      ))}
    </ul>
  </div>
);
